import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { format } from "date-fns";
import configSource from "../res/config.json";
import { pdfLayoutsByCountry } from "../logic/PdfPositions";
import {
  formatNumberForPdf,
  roundTotalMonthlyFeeByCountry,
} from "../logic/GeneralFunctions";
import { truncateText } from "../logic/GeneralFunctions";

export async function GenerateNewQuotePDF(props) {
  const regularPriceCountries = { se: true, uk: true };
  const today = new Date();
  const formattedDate = format(today, "yyyy-MM-dd");
  const lang = props.params.lang.toLowerCase();
  const formType = props.params.formType.toLowerCase();
  const isCeoraForm = props.params.isCeoraForm === true;

  const useRegularPrice =
    isCeoraForm && formType === "upcare" && regularPriceCountries[lang];

  let discountAmount = 0;
  if (useRegularPrice) {
    Object.values(props.productsValues).forEach((product) => {
      const accessoryPriceSum = (product.accessoryPrice || []).reduce(
        (sum, price) => sum + price,
        0,
      );
      const regularProductPrice =
        product.price * product.quantity + accessoryPriceSum;
      discountAmount += regularProductPrice - product.totalProductPrice;
    });
  }
  const hasDiscount = useRegularPrice && discountAmount > 0;

  const url =
    configSource.ROOT +
    `getimage?company=hcs&country=${lang}&type=quote-pdf/${formType}/quote_template${
      isCeoraForm ? "_ceora" : ""
    }${hasDiscount ? "_discount" : ""}.pdf`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Error fetching PDF: ${response.statusText}`);
  }

  const existingPdfBytes = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  const secondPage = pages[1];
  const roundedFee = roundTotalMonthlyFeeByCountry(
    lang,
    props.formInformation.totalMonthlyFee,
    formType,
  );
  const formattedFee = formatNumberForPdf(roundedFee, lang, formType);
  const digitCount = roundedFee.toString().length;

  //DISCOUNT CEORA CAMPAIGN
  let roundedRegularMonthlyFee = 0;
  let formattedRegularMonthlyFee = "";
  let regularPriceDigitCount = 0;
  let formattedDiscountAmount = "";
  if (hasDiscount) {
    const regularTotalMonthlyFee =
      props.formInformation.totalMonthlyFee + discountAmount;
    roundedRegularMonthlyFee = roundTotalMonthlyFeeByCountry(
      lang,
      regularTotalMonthlyFee,
      formType,
    );
    formattedRegularMonthlyFee = formatNumberForPdf(
      roundedRegularMonthlyFee,
      lang,
      formType,
    );
    regularPriceDigitCount = Math.trunc(roundedRegularMonthlyFee).toString()
      .length;

    const discountDifference = roundedRegularMonthlyFee - roundedFee;
    const roundedDiscountAmount = roundTotalMonthlyFeeByCountry(
      lang,
      discountDifference,
      formType,
    );
    formattedDiscountAmount = formatNumberForPdf(
      roundedDiscountAmount,
      lang,
      formType,
    );
  }

  const countryLayout = pdfLayoutsByCountry[formType][lang] || "";

  const firstPageLayout = countryLayout.firstPage;
  const secondPageLayout = countryLayout.secondPage;

  const currencySymbol = countryLayout.currency;
  const dynamicCurrencyCountries = { se: true, no: true, dk: true };
  const useDynamicCurrency = dynamicCurrencyCountries[lang];

  const expirationDate = isCeoraForm ? "2026-12-15." : formattedDate + ".";

  const firstPageDate = hasDiscount
    ? firstPageLayout.date.ceoraCampaignDiscount
    : isCeoraForm
      ? firstPageLayout.date.ceoraCampaign
      : firstPageLayout.date;

  const secondPageDate = isCeoraForm
    ? secondPageLayout.date.ceoraCampaign
    : secondPageLayout.date;

  //FIRST PAGE
  firstPage.drawText(formattedFee || "", {
    x: firstPageLayout.totalMonthlyFee.x,
    y: firstPageLayout.totalMonthlyFee.y,
    size: firstPageLayout.totalMonthlyFee.size,
    font,
    color: rgb(1, 1, 1),
  });
  if (useDynamicCurrency && firstPageLayout.currencyPosition) {
    firstPage.drawText(currencySymbol || "", {
      x: firstPageLayout.currencyPosition.x[digitCount] || 515,
      y: firstPageLayout.currencyPosition.y,
      size: firstPageLayout.currencyPosition.size,
      font: fontBold,
      color: rgb(1, 1, 1),
    });
  }

  firstPage.drawText(expirationDate || "", {
    x: firstPageDate.x,
    y: firstPageDate.y,
    size: firstPageLayout.date.size,
    font,
    color: rgb(0, 0, 0),
  });

  //DISCOUNT CEORA CAMPAIGN FIRST PAGE
  if (hasDiscount && firstPageLayout.regularPrice) {
    const regularPriceLayout = firstPageLayout.regularPrice;

    firstPage.drawText(formattedRegularMonthlyFee || "", {
      x: regularPriceLayout.x,
      y: regularPriceLayout.y,
      size: regularPriceLayout.size,
      font,
      color: rgb(1, 1, 1),
    });

    if (useDynamicCurrency && regularPriceLayout.currencyPosition) {
      firstPage.drawText(currencySymbol || "", {
        x: regularPriceLayout.currencyPosition.x[regularPriceDigitCount] || 515,
        y: regularPriceLayout.currencyPosition.y,
        size: regularPriceLayout.currencyPosition.size,
        font: fontBold,
        color: rgb(1, 1, 1),
      });
    }
    firstPage.drawLine({
      start: { x: regularPriceLayout.line.x1, y: regularPriceLayout.line.y1 },
      end: {
        x:
          regularPriceLayout.line.x2[regularPriceDigitCount] ||
          regularPriceLayout.line.x2[3], //Fallback to 3 digits if not specified
        y: regularPriceLayout.line.y2,
      },
      thickness: regularPriceLayout.line.thickness,
      color: rgb(1, 1, 1),
      opacity: 0.75,
    });
  }

  if (hasDiscount && firstPageLayout.savings) {
    firstPage.drawText(`${formattedDiscountAmount} ${props.labels.perMonth}`, {
      x: firstPageLayout.savings.x,
      y: firstPageLayout.savings.y,
      size: firstPageLayout.savings.size,
      font: fontBold,
      color: rgb(1, 1, 1),
    });
  }

  // SECOND PAGE
  secondPage.drawText(formattedFee || "", {
    x: secondPageLayout.totalMonthlyFee.x,
    y: secondPageLayout.totalMonthlyFee.y,
    size: secondPageLayout.totalMonthlyFee.size,
    font,
    color: rgb(0, 0, 0),
  });
  if (useDynamicCurrency && secondPageLayout.currencyPosition)
    secondPage.drawText(currencySymbol || "", {
      x: secondPageLayout.currencyPosition.x[digitCount] || 118,
      y: secondPageLayout.currencyPosition.y,
      size: secondPageLayout.currencyPosition.size,
      font: fontBold,
      color: rgb(0, 0, 0),
    });

  secondPage.drawText(props.formInformation.contractLength.toString() || "", {
    x: secondPageLayout.contractLength.x,
    y: secondPageLayout.contractLength.y,
    size: secondPageLayout.contractLength.size,
    font,
    color: rgb(0, 0, 0),
  });

  secondPage.drawText(expirationDate || "", {
    x: secondPageDate.x,
    y: secondPageDate.y,
    size: secondPageLayout.date.size,
    font,
    color: rgb(0, 0, 0),
  });

  //CUSTOMER INFO
  const customerLayout = secondPageLayout.customerInfo;

  secondPage.drawText(
    props.customerInformation.customerName.toUpperCase() || "",
    {
      x: customerLayout.customerName.x,
      y: customerLayout.customerName.y,
      size: customerLayout.customerName.size,
      font,
      color: rgb(0, 0, 0),
    },
  );

  secondPage.drawText(props.customerInformation.customerEmail || "", {
    x: customerLayout.customerEmail.x,
    y: customerLayout.customerEmail.y,
    size: customerLayout.customerEmail.size,
    font,
    color: rgb(0, 0, 0),
  });

  if (customerLayout.companyNumber) {
    secondPage.drawText(
      props.customerInformation.companyNumber.toUpperCase() || "",
      {
        x: customerLayout.companyNumber.x,
        y: customerLayout.companyNumber.y,
        size: customerLayout.companyNumber.size,
        font,
        color: rgb(0, 0, 0),
      },
    );
  }

  //DEALER INFO
  const dealerLayout = secondPageLayout.dealerInfo;

  secondPage.drawText(
    props.customerInformation.dealerName.toUpperCase() || "",
    {
      x: dealerLayout.dealerName.x,
      y: dealerLayout.dealerName.y,
      size: dealerLayout.dealerName.size,
      font,
      color: rgb(0, 0, 0),
    },
  );

  secondPage.drawText(props.customerInformation.dealerEmail || "", {
    x: dealerLayout.dealerEmail.x,
    y: dealerLayout.dealerEmail.y,
    size: dealerLayout.dealerEmail.size,
    font,
    color: rgb(0, 0, 0),
  });

  //PRODUCT

  const productsLayout = secondPageLayout.products;
  const startYProducts = productsLayout.startY;
  const rowHeightProducts = productsLayout.rowHeight;

  Object.values(props.productsValues).forEach((product, index) => {
    const currentY = startYProducts - index * rowHeightProducts;

    secondPage.drawText(product.quantity.toString() || "", {
      x: productsLayout.quantity.x,
      y: currentY,
      size: 8,
      font,
      color: rgb(0, 0, 0),
    });

    secondPage.drawText(truncateText(product.name, 55) || "", {
      x: productsLayout.name.x,
      y: currentY,
      size: productsLayout.size || 7.2,
      font,
      color: rgb(0, 0, 0),
    });

    if (
      (props.params.formType === "leaseplus" ||
        props.params.formType === "upcare") &&
      product.name !== props.labels.accessory
    ) {
      secondPage.drawText("Husqvarna services", {
        x: productsLayout.services.x,
        y: currentY,
        size: productsLayout.services.size || 7.5,
        font,
        color: rgb(0, 0, 0),
      });
    }
    if (product.name !== props.labels.accessory)
      secondPage.drawText(props.labels.positive_answer || "", {
        x: productsLayout.answer.x,
        y: currentY,
        size: productsLayout.services.size || 7.5,
        font,
        color: rgb(0, 0, 0),
      });
  });

  //ACCESORY
  const accessoriesLayout = secondPageLayout.accessories;
  const startYAccessories = accessoriesLayout.startY;
  const rowHeightAccessories = accessoriesLayout.rowHeight;

  if (props.params.formType === "leaseplus") {
    let accessoryIndex = 0;

    Object.values(props.productsValues).forEach((product, productIndex) => {
      if (product.accessory && product.accessory.length > 0) {
        product.accessory.forEach((accessoryName) => {
          if (!accessoryName || accessoryName.trim() === "") return;
          const currentY =
            startYAccessories - accessoryIndex * rowHeightAccessories;

          secondPage.drawText("1", {
            x: accessoriesLayout.quantity.x,
            y: currentY,
            size: 8,
            font,
            color: rgb(0, 0, 0),
          });

          secondPage.drawText(accessoryName || "", {
            x: accessoriesLayout.name.x,
            y: currentY,
            size: accessoriesLayout.size || 7.5,
            font,
            color: rgb(0, 0, 0),
          });

          accessoryIndex++;
        });
      }
    });
  } else {
    //Upcare
    const hasAccessories =
      props.accessories && Object.keys(props.accessories).length > 0;
    if (hasAccessories) {
      Object.values(props.accessories).forEach((accessory, index) => {
        const currentY = startYAccessories - index * rowHeightAccessories;

        secondPage.drawText(accessory.AccessoriesQuantity.toString() || "", {
          x: accessoriesLayout.quantity.x,
          y: currentY,
          size: 8,
          font,
          color: rgb(0, 0, 0),
        });

        secondPage.drawText(accessory.AccessoriesName || "", {
          x: accessoriesLayout.name.x,
          y: currentY,
          size: accessoriesLayout.size || 7.5,
          font,
          color: rgb(0, 0, 0),
        });
      });
    }
  }

  // Save
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}
